import { useCallback, useEffect, useMemo } from "react";
import { useAppContext } from "../../hooks/useAppContext";
import { useBeforeUnload } from "../../hooks/useBeforeUnload";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useGetIsNavigateAwayAllowed } from "../../hooks/useNavigate";

const label = {
	label: "Label.HTML5Editor.NavigateAway",
	default: "You have unsaved changes, are you sure you want to leave?",
};

export function useAppUnload(onBeforeUnloadCallback?: (message: string) => void) {
	const { editor, localizedContent } = useAppContext();

	const { createErrorHandler } = useErrorHandling();

	const mutable = useMemo(
		() => ({
			message: label.default,
		}),
		[],
	);

	useEffect(() => {
		localizedContent
			.getKeyAsync(label)
			.then((value: string) => {
				mutable.message = value;
			})
			.catch(createErrorHandler());
	}, [createErrorHandler, localizedContent, mutable]);

	const onBeforeUnload = useCallback(() => {
		const { message } = mutable;
		if (onBeforeUnloadCallback) {
			onBeforeUnloadCallback(message);
		}
		return message;
	}, [mutable, onBeforeUnloadCallback]);

	const getIsNavigateAwayAllowed = useGetIsNavigateAwayAllowed();
	const isThereAnyPopupOpen = !!document.querySelector("div.dialog-container");

	const isBeforeUnloadEnabled = useCallback(
		() =>
			!getIsNavigateAwayAllowed() && // Don't show popup if allowed to navigate away
			(editor.isDirty() || // Show popup when product not yet saved
				editor.hasPendingUploads() || // Show popup if still uploading
				isThereAnyPopupOpen), // Show popup when there is a popup open
		[getIsNavigateAwayAllowed, isThereAnyPopupOpen, editor],
	);

	useBeforeUnload(isBeforeUnloadEnabled, onBeforeUnload);
}
